import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { closeModal, showPageLoading, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import defaultThumbnailBase64 from 'assets/images/thumbnail-default.png';

import type { FunctionalTest, ScoringRuleItem } from '../type';

import { getExtFileName, hasEmptyValues, randomString, trimObject } from 'helpers';
import { base64ToFile } from 'helpers/convertBase64ToFile';
import { UploadFile } from 'helpers/upload-file';
import { useString } from 'hooks';
import { apiCall, closeLoading } from 'hooks/service';
import { useWidthAndHeightFile } from 'hooks/widthAndHeightFile';
import { cloneDeep, isEmpty, orderBy } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { defaultFunctionalTest, defaultScoringRule } from '../constant';
import { FunctionalTestMethod, FunctionalTestType } from '../enum';

const DEFAULT_SCORE_RULE = 0.001;

export const useFunctionalTestFormController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { id, institutionId } = params;

  const userProfile = useAuthStore(userProfileSelector);

  const isInstitutionDetail = location.pathname.includes(`${PATH.INSTITUTIONS}`);

  const isFunctionalTestAdd = location.pathname.includes(`${PATH.ADD_FUNCTIONAL_TESTS}`);

  const institution_id = userProfile?.institution?.id;

  const [video, setVideo] = useState<File | null>(null);

  const [thumbnail, setThumbnail] = useState<File | null>(null);

  const videoUrl = useString();

  const thumbnailUrl = useString();

  const {
    handleGetWidthAndHeightThumbnail,
    handleGetWidthAndHeightVideo,
    thumbnailHeight,
    thumbnailWidth,
    videoHeight,
    videoWidth,
  } = useWidthAndHeightFile();

  const [functionalTestDetail, setFunctionalTestDetail] = useState<FunctionalTest>();

  const [selectedData, setSelectedData] = useState<ScoringRuleItem[]>([]);

  const [deleteSelected, setDeleteSelected] = useState<ScoringRuleItem[]>([]);

  const [protocol, setProtocol] = useState(EditorState.createEmpty());

  useEffect(() => {
    if (isFunctionalTestAdd) {
      return;
    }

    if (id) {
      getFunctionalTestDetail();
    }
  }, [id]);

  const getFunctionalTestDetail = async () => {
    const result: FunctionalTest = await apiCall({
      url: `/functional-tests/${id}`,
      method: 'GET',
      isLoading: true,
    });

    if (result?.id) {
      const newScoreRules = result.scoreRules.map((el) => ({
        min: Number(el.min) / 1000,
        max: Number(el.max) / 1000,
        score: el.score,
        id: el.id,
        parentId: el.parentId,
      }));

      setValues({
        ...result,
        scoreRules: newScoreRules,
      } as any);
      setFieldValue('type', result.type);
      setFieldValue('method', result.method);
      setFieldValue(
        'thresholdTime',
        result.thresholdTime ? Number(result.thresholdTime) / 1000 : undefined,
      );
      setFunctionalTestDetail({
        ...result,
      } as any);
      videoUrl.setValue(result?.video ?? '');
      thumbnailUrl.setValue(result?.thumbnail ?? '');

      setFieldValue('isSO2AndHR', result.isSO2AndHR);
      setFieldValue('isAddition', result.isAddition);

      if (result?.scoreRules.length > 0) {
        setSelectedData(orderBy(newScoreRules, 'min'));
      }

      const contentState = convertFromRaw(JSON.parse(result.protocol));

      setProtocol(EditorState.createWithContent(contentState));
    }
  };

  const goBack = () => {
    navigationTo(-1);
  };

  const formik = useFormik({
    initialValues: defaultFunctionalTest,

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      showPageLoading();

      const resultUpload: {
        urlVideo: string;
        urlThumbnail: string;
        nameFileImage?: string;
        nameFileVideo?: string;
      } | null = await handleUploadVideoAndThumbnail();

      if (!resultUpload) {
        showToastMessage('error', 'Error upload video or thumbnail');

        return;
      }

      if (selectedData.some((el) => Number(el.score) > Number(values.maxScore))) {
        showToastMessage('error', 'Achieved score is invalid (more than max score).');

        closeLoading();

        return;
      }

      const apiRequest = trimObject({
        ...values,
        thresholdTime: Number(values.thresholdTime) * 1000,
        protocol: convertToRaw(protocol.getCurrentContent()),
        protocolHtml: draftToHtml(convertToRaw(protocol.getCurrentContent())),
        scoreRules:
          values.method.toLowerCase() === Object.keys(FunctionalTestMethod)[1].toLowerCase()
            ? selectedData.map((el) => ({
                ...el,
                min: Number(Math.ceil(Number(el.min) * 1000)),
                max: Number(Math.ceil(Number(el.max) * 1000)),
                score: Number(el.score),
              }))
            : [],
        video: resultUpload.nameFileVideo,
        thumbnail: resultUpload.nameFileImage,
        parentId: '',
        removeScoreRules:
          values.method.toLowerCase() === Object.keys(FunctionalTestMethod)[1].toLowerCase() &&
          values.type?.toLowerCase() === Object.keys(FunctionalTestType)[0].toLowerCase()
            ? deleteSelected.map((el) => el.id)
            : functionalTestDetail?.scoreRules.map((el) => el.id),
        institutionId: institution_id,
        maxScore:
          values.method.toLowerCase() === FunctionalTestMethod.Kg ||
          values.method.toLowerCase() === FunctionalTestMethod.Time
            ? null
            : values.maxScore,
        type:
          values.method.toLowerCase() === Object.keys(FunctionalTestMethod)[1].toLowerCase()
            ? values.type
            : null,
        isSO2AndHR:
          values.method.toLowerCase() === Object.keys(FunctionalTestMethod)[0].toLowerCase()
            ? values.isSO2AndHR
            : false,
        isAddition:
          values.method.toLowerCase() === Object.keys(FunctionalTestMethod)[1].toLowerCase()
            ? values.isAddition
            : false,
      });

      if (resultUpload.urlVideo && videoHeight.value && videoWidth.value) {
        apiRequest.videoHeight = videoHeight.value;
        apiRequest.videoWidth = videoWidth.value;
      }

      if (resultUpload.urlThumbnail && thumbnailHeight.value && thumbnailWidth.value) {
        apiRequest.thumbnailHeight = thumbnailHeight.value;
        apiRequest.thumbnailWidth = thumbnailWidth.value;
      }

      if (!resultUpload.nameFileVideo) {
        delete apiRequest.video;
      }

      if (!resultUpload.nameFileImage) {
        delete apiRequest.thumbnail;
      }

      if (isFunctionalTestAdd && isInstitutionDetail) {
        apiRequest.institutionId = institutionId;
      }

      const result: FunctionalTest = await apiCall({
        url: !isFunctionalTestAdd ? `/functional-tests/${id}` : `/functional-tests`,
        method: !isFunctionalTestAdd ? 'PATCH' : 'POST',
        data: {
          ...apiRequest,
        },

        isLoading: true,
        showError: true,
      });

      if (!result?.id) {
        closeLoading();

        return;
      }

      if (!isFunctionalTestAdd) {
        getFunctionalTestDetail();
        showToastMessage('success', SUCCESS_MESSAGE.UPDATE_FUNCTIONAL_TEST);

        return;
      }

      closeLoading();
      showToastMessage('success', SUCCESS_MESSAGE.ADD_FUNCTIONAL_TEST);
      formik.setValues(defaultFunctionalTest);
      navigationTo(-1);
    },
  });

  const handleUploadVideoAndThumbnail = async () => {
    let urlVideo = videoUrl.value;
    let urlThumbnail = thumbnailUrl.value;

    let nameFileVideo = '';

    let nameFileImage = '';

    try {
      if (video) {
        const ext = getExtFileName(video.name);

        nameFileVideo = `video-functional-test-${randomString(4)}-${Date.now()}${ext}`;

        urlVideo = await UploadFile(video, nameFileVideo);
      }

      if (thumbnail) {
        const thumbnailExt = getExtFileName(thumbnail.name);

        nameFileImage = `thumbnail-functional-test-${randomString(4)}-${Date.now()}${thumbnailExt}`;

        urlThumbnail = await UploadFile(thumbnail, nameFileImage);
      }

      if (!thumbnail && !urlThumbnail) {
        const defaultThumbnailFile = base64ToFile(
          defaultThumbnailBase64,
          'default-thumbnail.png',
          'image/png',
        );

        const defaultThumbnailExt = getExtFileName(defaultThumbnailFile.name);

        nameFileImage = `thumbnail-default${defaultThumbnailExt}`;

        urlThumbnail = await UploadFile(defaultThumbnailFile, nameFileImage);
      }

      return {
        urlVideo,
        urlThumbnail,
        nameFileImage,
        nameFileVideo,
      };
    } catch (error) {
      console.log(error);

      return null;
    }
  };

  const { setFieldValue, handleChange, handleSubmit, values, setValues, isSubmitting, isValid } =
    formik;

  const showScoreRules =
    values?.method?.toLowerCase() === Object.keys(FunctionalTestMethod)[1].toLowerCase() &&
    values.type?.toLowerCase() === Object.keys(FunctionalTestType)[0].toLowerCase();

  const showScoringType =
    values?.method?.toLowerCase() === Object.keys(FunctionalTestMethod)[1].toLowerCase();

  const disabled =
    hasEmptyValues({
      name: values.name,
      method: values.method,
    }) ||
    isSubmitting ||
    !isValid ||
    // !thumbnailUrl.value ||
    !videoUrl.value ||
    // !protocol.getCurrentContent().hasText() ||
    (showScoreRules && isEmpty(selectedData)) ||
    (showScoringType && !values.type) ||
    (values.method.toLowerCase() !== FunctionalTestMethod.Kg &&
    values.method.toLowerCase() !== FunctionalTestMethod.Time
      ? !values.maxScore
      : false);

  const isFunctionalTestDetailEqual =
    JSON.stringify({
      name: functionalTestDetail?.name,
      maxScore: functionalTestDetail?.maxScore,
      thresholdTime: Number(functionalTestDetail?.thresholdTime) / 1000,
      method: functionalTestDetail?.method,
      thumbnail: functionalTestDetail?.thumbnail,
      video: functionalTestDetail?.video,
      type: functionalTestDetail?.type,
      scoreRules: orderBy(
        functionalTestDetail?.scoreRules.map((el) => ({
          min: Number(el.min) / 1000,
          max: Number(el.max) / 1000,
          score: el.score,
        })),
        'min',
      ),
      protocol: functionalTestDetail?.protocol,
      isSO2AndHR: functionalTestDetail?.isSO2AndHR,
      isAddition: functionalTestDetail?.isAddition,
    }) ===
    JSON.stringify({
      name: values?.name,
      maxScore: values?.maxScore,
      thresholdTime: values?.thresholdTime ?? 0,
      method: values?.method,
      thumbnail: thumbnailUrl.value,
      video: videoUrl.value,
      type: values.type,
      scoreRules: orderBy(
        selectedData.map((el) => ({
          min: el.min,
          max: el.max,
          score: el.score,
        })),
        'min',
      ),
      protocol: JSON.stringify(convertToRaw(protocol.getCurrentContent())),
      isSO2AndHR: values.isSO2AndHR,
      isAddition: values.isAddition,
    });

  const disabledButton = useMemo(() => {
    return (
      isFunctionalTestDetailEqual ||
      hasEmptyValues({
        name: values?.name,
      }) ||
      // !thumbnailUrl.value ||
      !videoUrl.value ||
      // !protocol.getCurrentContent().hasText() ||
      (showScoreRules && isEmpty(selectedData)) ||
      (showScoringType && !values.type) ||
      (values.method.toLowerCase() !== FunctionalTestMethod.Kg &&
      values.method.toLowerCase() !== FunctionalTestMethod.Time
        ? !values.maxScore
        : false)
    );
  }, [functionalTestDetail, selectedData, values, thumbnailUrl.value, videoUrl.value, protocol]);

  const onChangeType = (newTypeKey: string) => () => {
    setFieldValue('type', newTypeKey);

    setSelectedData([]);
  };

  const onChangeMetric = (item: any) => {
    if (!item) {
      return;
    }

    setFieldValue('method', item.key);
    setFieldValue('type', '');
    setSelectedData([]);
    setFieldValue('isAddition', false);
  };

  const handleRemoveVideo = () => {
    setVideo(null);
    videoUrl.setValue('');
  };

  const onChangeVideo = (files: File[]) => {
    const file = files[0];

    if (file && file.type.includes('video')) {
      setVideo(file);
      videoUrl.setValue(URL.createObjectURL(file));
    } else {
      showToastMessage('error', 'Error format video');
    }
  };

  const handleRemoveThumbnail = () => {
    setThumbnail(null);

    if (functionalTestDetail?.thumbnail?.includes('thumbnail-default')) {
      thumbnailUrl.setValue(functionalTestDetail?.thumbnail);
    } else {
      thumbnailUrl.setValue('');
    }
  };

  const onChangeThumbnail = (files: File[]) => {
    const file = files[0];

    if (file && file.type.includes('image')) {
      setThumbnail(file);
      thumbnailUrl.setValue(URL.createObjectURL(file));
    } else {
      showToastMessage('error', 'Error format image');
    }
  };

  const onPreventDefault = () => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
    event.preventDefault();

  const onCancel = () => {
    onPreventDefault();

    if (!isFunctionalTestAdd && id) {
      setValues({
        ...functionalTestDetail,
        thresholdTime: functionalTestDetail?.thresholdTime
          ? Number(functionalTestDetail.thresholdTime) / 1000
          : undefined,
      } as any);
      setFieldValue('type', functionalTestDetail?.type);
      setFieldValue('method', functionalTestDetail?.method);
      videoUrl.setValue(functionalTestDetail?.video ?? '');
      thumbnailUrl.setValue(functionalTestDetail?.thumbnail ?? '');
      setSelectedData(
        orderBy(
          functionalTestDetail?.scoreRules.map((el) => ({
            ...el,
            min: Number(el.min) / 1000,
            max: Number(el.max) / 1000,
            score: Number(el.score),
          })),
          'min',
        ),
      );

      const contentState = convertFromRaw(JSON.parse(functionalTestDetail?.protocol as string));

      setProtocol(EditorState.createWithContent(contentState));

      return;
    }

    formik.setValues(defaultFunctionalTest);
    navigationTo(-1);
  };

  const handleAddScoreRule = () => {
    setSelectedData([
      ...selectedData,
      {
        min: selectedData[selectedData.length - 1]
          ? Number(
              (Number(selectedData[selectedData.length - 1].max) + DEFAULT_SCORE_RULE).toFixed(3),
            )
          : 0,
        max: selectedData[selectedData.length - 1]
          ? Number(
              (Number(selectedData[selectedData.length - 1].max) + DEFAULT_SCORE_RULE).toFixed(3),
            )
          : 0,
        score: 0,
        id: '',
        parentId: '',
      },
    ]);
  };

  const handleRemoveScoreRule = (index: number) => {
    const newSelectedData = cloneDeep(selectedData);

    setDeleteSelected([...deleteSelected, newSelectedData[index]]);

    newSelectedData.splice(index, 1);

    setSelectedData(newSelectedData);
  };

  const onChangeValueScoreRules = (index: number, value: Record<string, string>) => {
    const newSelectedData = [...selectedData];

    newSelectedData[index] = { ...newSelectedData[index], ...value };

    if (newSelectedData.length > 1) {
      if (newSelectedData.length !== index + 1) {
        newSelectedData[index + 1].min = Number(
          (Number(newSelectedData[index].max) + DEFAULT_SCORE_RULE).toFixed(3),
        );
      }
    }

    setSelectedData(newSelectedData);
  };

  const onChangeProtocol = (editorState: EditorState) => {
    setProtocol(editorState);
  };

  const onDeleteFunctionalTest = async () => {
    await apiCall({
      url: `/functional-tests/${id}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });

    closeModal(MODAL_KEY.DELETE_FUNCTIONAL_TEST);

    showToastMessage('success', SUCCESS_MESSAGE.DELETE_FUNCTIONAL_TEST);

    navigationTo(-1);
  };

  const onChangeSwitch = () => {
    setFieldValue('isSO2AndHR', !values.isSO2AndHR);
  };

  const onChangeAddition = () => {
    setFieldValue('isAddition', !values.isAddition);
  };

  const showSpO2AndHR =
    values?.method?.toLowerCase() === Object.keys(FunctionalTestMethod)[0].toLowerCase();

  const hideMaxScoreAndThresholdTime =
    values?.method?.toLowerCase() === FunctionalTestMethod.Kg ||
    values?.method?.toLowerCase() === FunctionalTestMethod.Time ||
    !values.method;

  return {
    goBack,
    onChangeType,
    videoUrl,
    thumbnailUrl,
    handleRemoveThumbnail,
    handleRemoveVideo,
    onChangeVideo,
    onChangeThumbnail,
    handleChange,
    handleSubmit,
    disabled,
    disabledButton,
    onCancel,
    values,
    functionalTestId: isFunctionalTestAdd ? '' : id,
    functionalTestDetail,
    onChangeMetric,
    handleAddScoreRule,
    handleRemoveScoreRule,
    selectedData,
    onChangeValueScoreRules,
    onChangeProtocol,
    protocol,
    onDeleteFunctionalTest,
    showScoreRules,
    showScoringType,
    onChangeSwitch,
    showSpO2AndHR,
    hideMaxScoreAndThresholdTime,
    handleGetWidthAndHeightVideo,
    handleGetWidthAndHeightThumbnail,
    onChangeAddition,
    isFunctionalTestDetailEqual,
  };
};
